import React, { useState } from 'react';
import { WebSearchData } from '@/types/webSearch';
import { KeyContact } from '@/types/lead';
import { MapPin, Phone, Mail, Globe, Star, ExternalLink, ChevronDown, ChevronUp, Briefcase, Handshake, Heart, User } from 'lucide-react';
import { formatDateYMD } from '@/utils/dateFormat';

interface WebSearchDetailsProps {
  searchData: WebSearchData;
  leadName?: string;
  keyContacts?: KeyContact[];
  leadAddress?: string;
  leadPhone?: string;
  leadEmail?: string;
  leadWebsite?: string;
}

const GOOGLE_PLACES_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

const getHostname = (url?: string) => {
  if (!url) return null;
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '');
  }
};

const TextOrPlaceholder: React.FC<{ children?: React.ReactNode }> = ({ children }) =>
  children ? <>{children}</> : <span className="text-muted-foreground italic">Details not available</span>;

const safeStringify = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object' && value.email) {
    return value.type ? `${value.email} (${value.type})` : value.email;
  }
  if (typeof value === 'object') {
    if (value.name) return String(value.name);
    if (value.title) return String(value.title);
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
};

const BulletList: React.FC<{ items?: any[] }> = ({ items }) => {
  if (!items || items.length === 0) {
    return <p className="text-muted-foreground italic">Details not available</p>;
  }
  return (
    <ul className="space-y-1">
      {items.map((it, i) => {
        const text = safeStringify(it);
        if (!text) return null;
        return (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-1">•</span>
            <span>{text}</span>
          </li>
        );
      }).filter(Boolean)}
    </ul>
  );
};

const formatStaffNarrative = (keyPeople?: any[]): string => {
  if (!keyPeople || keyPeople.length === 0) {
    return 'Specific details about key personnel are not readily available.';
  }
  const people = keyPeople.map(p => {
    if (typeof p === 'string') {
      const match = p.match(/^([^-\(]+)(?:\s*[-\(]\s*([^\)]+))?/);
      if (match) return { name: match[1].trim(), role: match[2]?.replace(')', '').trim() };
      return { name: p.trim(), role: null };
    }
    if (typeof p === 'object') return { name: p.name || p.title || '', role: p.role || p.position || null };
    return { name: String(p), role: null };
  }).filter(p => p.name);

  if (people.length === 0) return 'Specific details about key personnel are not readily available.';
  const sentences: string[] = [];
  const [first, ...others] = people;
  sentences.push(first.role ? `${first.name} serves as ${first.role}.` : `${first.name} is a key member of the team.`);
  if (others.length > 0) {
    const otherNames = others.map(p => p.role ? `${p.name} (${p.role})` : p.name).join(', ').replace(/,([^,]*)$/, ' and$1');
    sentences.push(`Other team members include ${otherNames}.`);
  }
  sentences.push('Specific details about other key personnel are not readily available.');
  return sentences.join(' ');
};

const formatSupportNarrative = (supportChannels?: any[], phone?: string | null, email?: string | null, altPhone?: string | null): string => {
  if (!supportChannels || supportChannels.length === 0) {
    if (phone || email) {
      const parts = [];
      if (phone) parts.push(`phone (${phone})`);
      if (email) parts.push(`email (${email})`);
      return `Customers can reach out via ${parts.join(' or ')}.`;
    }
    return 'Specific contact methods are not readily available.';
  }
  const channels = supportChannels.map(ch => safeStringify(ch).toLowerCase());
  const hasPhone = channels.some(ch => ch.includes('phone') || ch.includes('telephone'));
  const hasEmail = channels.some(ch => ch.includes('email'));
  const hasOnline = channels.some(ch => ch.includes('online') || ch.includes('form') || ch.includes('website'));
  const hasComplaints = channels.some(ch => ch.includes('complaint'));
  const methods = [];
  if (hasPhone && phone) methods.push(`phone (${phone})`);
  if (hasEmail && email) methods.push(`email (${email})`);
  if (altPhone) methods.push(`alternative phone (${altPhone})`);
  if (hasOnline) methods.push('online contact forms');
  let narrative = methods.length > 0
    ? `Customers can reach out via ${methods.join(', ').replace(/,([^,]*)$/, ' or$1')}.`
    : 'The company provides multiple customer support channels.';
  if (hasComplaints) narrative += ' The company has a designated Complaints Officer available.';
  return narrative;
};

/** Collapsible list that shows first N items then toggles */
const CollapsibleList: React.FC<{ items: string[]; limit?: number; renderItem: (item: string, i: number) => React.ReactNode }> = ({ items, limit = 5, renderItem }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, limit);
  const hasMore = items.length > limit;
  return (
    <div className="space-y-1">
      {visible.map((item, i) => <React.Fragment key={i}>{renderItem(item, i)}</React.Fragment>)}
      {hasMore && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1">
          {expanded ? <><ChevronUp className="h-3 w-3" /> Show less</> : <><ChevronDown className="h-3 w-3" /> Show {items.length - limit} more</>}
        </button>
      )}
    </div>
  );
};

const WebSearchDetails: React.FC<WebSearchDetailsProps> = ({ searchData, leadName, keyContacts, leadAddress, leadPhone, leadEmail, leadWebsite }) => {
  if (!searchData) {
    return <p className="text-muted-foreground py-2">No details available.</p>;
  }

  // Deep research & crawl analysis shorthand
  const dr = (searchData as any).deepResearch;
  const ca = (searchData as any).crawlAnalysis;

  // Field precedence and normalization — fall back to lead's own DB fields
  const address = searchData.contactDetails?.address || searchData.places?.address || leadAddress || null;
  const phone = searchData.contactDetails?.phone || searchData.linkedIn?.phone || searchData.places?.phone || leadPhone || null;
  const email = searchData.contactDetails?.email || searchData.linkedIn?.email || leadEmail || null;

  let altPhone: string | null = null;
  if (Array.isArray(searchData.supportChannels)) {
    const altEntry = searchData.supportChannels.find(
      (ch: any) => typeof ch === 'string' && /alternative/i.test(ch) && /\d{3,}/.test(ch)
    );
    if (typeof altEntry === 'string') {
      const match = altEntry.match(/(\+?\d[\d\s\-()]{6,})/);
      altPhone = match ? match[1].trim() : altEntry;
    }
  }

  const websiteRaw = searchData.contactDetails?.website || searchData.linkedIn?.website || searchData.places?.website || leadWebsite || null;
  const websiteHost = getHostname(websiteRaw);

  const overview = searchData.overview || searchData.summary || searchData.linkedIn?.companyDescription || '';

  const placeId = searchData.places?.placeId;
  const reviews = searchData.places?.reviews || [];
  const rating = searchData.places?.rating ?? searchData.reviews?.rating;
  const reviewsCount = searchData.places?.reviewsCount ?? searchData.reviews?.count;

  // === MERGED DATA from all three sources ===

  // Emails: combine all unique (including key contacts)
  const allEmails = Array.from(new Set([
    email,
    ...(dr?.additionalEmails || []),
    ...(ca?.additionalEmails || []),
    ...(keyContacts?.map(c => c.email).filter(Boolean) || []),
  ].filter(Boolean))) as string[];

  // Phones: combine all unique (including key contacts)
  const allPhones = Array.from(new Set([
    phone,
    altPhone,
    ...(dr?.additionalPhones || []),
    ...(ca?.additionalPhones || []),
    ...(keyContacts?.map(c => c.phone).filter(Boolean) || []),
  ].filter(Boolean))) as string[];

  // Services: crawl practiceAreas > DR serviceAreas > web search services
  const mergedServices = ca?.practiceAreas?.length ? ca.practiceAreas
    : dr?.serviceAreas?.length ? dr.serviceAreas
    : searchData.services;

  // Overview: crawl > DR > web search
  const mergedOverview = ca?.companyOverview || dr?.companyOverview || overview;

  // Company details
  const mergedFoundedYear = ca?.foundedYear || searchData.foundedYear;
  const mergedEmployeeCount = ca?.employeeCount || searchData.companySize;

  // Office locations from crawl
  const officeLocations: any[] = ca?.officeLocations || [];

  // Certifications
  const certifications = Array.from(new Set([...(dr?.certifications || []), ...(ca?.certifications || [])]));

  // Partners
  const partners: string[] = ca?.partnerOrganizations || [];

  // Job openings
  const jobOpenings: string[] = ca?.jobOpenings?.length ? ca.jobOpenings : dr?.jobOpenings || [];

  // Social media: merge crawl > web search
  const mergedSocial = {
    facebook: ca?.socialMedia?.facebook || searchData.socialMedia?.facebook,
    linkedin: ca?.socialMedia?.linkedin || searchData.socialMedia?.linkedin || searchData.linkedIn?.companyPageUrl,
    twitter: ca?.socialMedia?.twitter || searchData.socialMedia?.twitter,
    instagram: ca?.socialMedia?.instagram || searchData.socialMedia?.instagram,
  };

  // Company values
  const companyValues: string[] = ca?.companyValues || [];

  // Verified-source hints
  const sources: string[] = [];
  if (websiteRaw) sources.push('Official Website');
  if (searchData.linkedIn?.companyPageUrl) sources.push('LinkedIn');
  if (searchData.places?.mapsUrl || searchData.places?.placeId) sources.push('Google Maps');
  if (dr) sources.push('Deep Research');
  if (ca) sources.push('Website Crawl');

  return (
    <div className="space-y-8 leading-relaxed">
      {/* Header Information */}
      <div className="rounded-md border p-4 bg-primary/5">
        <h2 className="text-lg font-semibold mb-2">
          <TextOrPlaceholder>{leadName}</TextOrPlaceholder>
        </h2>
        <div className="space-y-2 text-[15px]">
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
            <TextOrPlaceholder>{address}</TextOrPlaceholder>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <TextOrPlaceholder>
                {phone ? <a className="text-blue-600 hover:underline" href={`tel:${phone}`}>{phone}</a> : null}
              </TextOrPlaceholder>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <TextOrPlaceholder>
                {allEmails.length > 0 ? <a className="text-blue-600 hover:underline" href={`mailto:${allEmails[0]}`}>{allEmails[0]}</a> : null}
              </TextOrPlaceholder>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <TextOrPlaceholder>
                {websiteRaw ? (
                  <a className="text-blue-600 hover:underline" href={websiteRaw.startsWith('http') ? websiteRaw : `https://${websiteRaw}`} target="_blank" rel="noopener noreferrer">
                    {websiteHost}
                  </a>
                ) : null}
              </TextOrPlaceholder>
            </div>
          </div>

          {/* Action Labels */}
          <div className="flex gap-2 mt-2 flex-wrap">
            <a
              className={`px-2 py-1 rounded border text-xs ${phone ? 'hover:bg-muted' : 'text-muted-foreground cursor-not-allowed'}`}
              href={phone ? `tel:${phone}` : undefined}
              aria-disabled={!phone}
            >
              Call
            </a>
            <a
              className={`px-2 py-1 rounded border text-xs ${allEmails.length > 0 ? 'hover:bg-muted' : 'text-muted-foreground cursor-not-allowed'}`}
              href={allEmails.length > 0 ? `mailto:${allEmails[0]}` : undefined}
              aria-disabled={allEmails.length === 0}
            >
              Email
            </a>
            <a
              className={`px-2 py-1 rounded border text-xs inline-flex items-center gap-1 ${websiteRaw ? 'hover:bg-muted' : 'text-muted-foreground cursor-not-allowed'}`}
              href={websiteRaw ? (websiteRaw.startsWith('http') ? websiteRaw : `https://${websiteRaw}`) : undefined}
              target={websiteRaw ? '_blank' : undefined}
              rel={websiteRaw ? 'noopener noreferrer' : undefined}
              aria-disabled={!websiteRaw}
            >
              Visit Website
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {sources.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2">Verified sources: {sources.join(' • ')}</p>
          )}
        </div>
      </div>

      {/* Overview Summary */}
      <div>
        <h3 className="text-base font-bold mb-2">{leadName} — Overview</h3>
        <p className="text-[15px]">
          <TextOrPlaceholder>{mergedOverview}</TextOrPlaceholder>
        </p>
      </div>

      {/* 1. Services, Products, and Business Offerings */}
      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">1. Services, Products, and Business Offerings</h4>
        <div className="space-y-3">
          <BulletList items={mergedServices} />
          {searchData.products && searchData.products.length > 0 && (
            <BulletList items={searchData.products} />
          )}
          {!mergedServices && !searchData.products && <p className="text-muted-foreground italic">Details not available</p>}
        </div>
      </section>

      {/* 2. Company Details */}
      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">2. Company Details</h4>
        <div className="space-y-2">
          {mergedFoundedYear && (
            <div className="flex items-start gap-2">
              <span className="font-medium">Founded:</span>
              <span>{mergedFoundedYear}</span>
            </div>
          )}
          {mergedEmployeeCount && (
            <div className="flex items-start gap-2">
              <span className="font-medium">Company Size:</span>
              <span>{mergedEmployeeCount}</span>
            </div>
          )}
          {searchData.linkedIn?.industry && (
            <div className="flex items-start gap-2">
              <span className="font-medium">Industry:</span>
              <span>{searchData.linkedIn.industry}</span>
            </div>
          )}
          {certifications.length > 0 && (
            <div>
              <span className="font-medium">Certifications:</span>
              <BulletList items={certifications} />
            </div>
          )}
          {companyValues.length > 0 && (
            <div>
              <span className="font-medium flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> Company Values:</span>
              <BulletList items={companyValues} />
            </div>
          )}
          {!mergedFoundedYear && !mergedEmployeeCount && !searchData.linkedIn?.industry && certifications.length === 0 && companyValues.length === 0 && (
            <p className="text-muted-foreground italic">Details not available</p>
          )}
        </div>
      </section>

      {/* 3. Industry Insights and Market Positioning */}
      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">3. Industry Insights and Market Positioning</h4>
        <BulletList items={searchData.industryInsights} />
        {searchData.marketPosition && (
          <p className="mt-3 text-[15px]">
            <TextOrPlaceholder>{searchData.marketPosition}</TextOrPlaceholder>
          </p>
        )}
      </section>

      {/* 4. Competitive Landscape */}
      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">4. Competitive Landscape</h4>
        {searchData.competitors && searchData.competitors.length > 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground mb-2">Main competitors:</p>
            <BulletList items={searchData.competitors} />
          </div>
        ) : (
          <p className="text-muted-foreground italic">Details not available</p>
        )}
      </section>

      {/* 4b. Key Partners and Suppliers */}
      {partners.length > 0 && (
        <section className="border-t pt-6">
          <h4 className="font-bold mb-2 flex items-center gap-2"><Handshake className="h-4 w-4" /> Key Partners and Suppliers</h4>
          <div className="flex flex-wrap gap-1.5">
            {partners.map((p, i) => (
              <span key={i} className="px-2 py-1 bg-muted rounded text-sm">{p}</span>
            ))}
          </div>
        </section>
      )}

      {/* 5. History, Reputation, and Competitive Advantages */}
      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">5. History, Reputation, and Competitive Advantages</h4>
        <div className="space-y-3">
          <p className="text-[15px]"><TextOrPlaceholder>{searchData.history || dr?.companyOverview || ca?.companyOverview || null}</TextOrPlaceholder></p>
          <p className="text-[15px]"><TextOrPlaceholder>{searchData.reputation}</TextOrPlaceholder></p>
        </div>
      </section>

      {/* 6. Contact Information */}
      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">6. Contact Information</h4>
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
            <TextOrPlaceholder>{address}</TextOrPlaceholder>
          </div>

          {/* All phones */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">Phone Numbers</span>
            </div>
            {allPhones.length > 0 ? (
              <CollapsibleList
                items={allPhones}
                limit={5}
                renderItem={(ph) => (
                  <div className="ml-6">
                    <a className="text-blue-600 hover:underline text-sm" href={`tel:${ph}`}>{ph}</a>
                  </div>
                )}
              />
            ) : (
              <p className="ml-6 text-muted-foreground italic text-sm">Details not available</p>
            )}
          </div>

          {/* All emails */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">Email Addresses</span>
            </div>
            {allEmails.length > 0 ? (
              <CollapsibleList
                items={allEmails}
                limit={5}
                renderItem={(em) => (
                  <div className="ml-6">
                    <a className="text-blue-600 hover:underline text-sm" href={`mailto:${em}`}>{em}</a>
                  </div>
                )}
              />
            ) : (
              <p className="ml-6 text-muted-foreground italic text-sm">Details not available</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" />
            <TextOrPlaceholder>
              {websiteRaw ? (
                <a className="text-blue-600 hover:underline" href={websiteRaw.startsWith('http') ? websiteRaw : `https://${websiteRaw}`} target="_blank" rel="noopener noreferrer">
                  {websiteHost}
                </a>
              ) : null}
            </TextOrPlaceholder>
          </div>
        </div>
      </section>

      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">7. Staff/Team Members</h4>
        {(() => {
          // Merge key people from all sources + actual DB key contacts
          const peopleFromSearch = (searchData.keyPeople || []).map((p: any) => {
            if (typeof p === 'string') {
              const match = p.match(/^([^-\(]+)(?:\s*[-\(]\s*([^\)]+))?/);
              return { name: match ? match[1].trim() : p.trim(), position: match?.[2]?.replace(')', '').trim() || '' };
            }
            if (typeof p === 'object') return { name: p.name || p.title || '', position: p.role || p.position || '' };
            return { name: String(p), position: '' };
          });
          const peopleFromDR = (dr?.keyPeople || []).map((p: any) => {
            if (typeof p === 'string') return { name: p.trim(), position: '' };
            if (typeof p === 'object') return { name: p.name || '', position: p.role || p.position || '' };
            return { name: String(p), position: '' };
          });
          const peopleFromCA = (ca?.keyPeople || []).map((p: any) => {
            if (typeof p === 'string') return { name: p.trim(), position: '' };
            if (typeof p === 'object') return { name: p.name || '', position: p.role || p.position || '' };
            return { name: String(p), position: '' };
          });
          const peopleFromContacts = (keyContacts || []).map(c => ({
            name: c.name,
            position: c.position || '',
            email: c.email || '',
            phone: c.phone || '',
            linkedinUrl: c.linkedinUrl || '',
          }));

          // Deduplicate by lowercase name
          const seen = new Set<string>();
          const allPeople: Array<{ name: string; position: string; email?: string; phone?: string; linkedinUrl?: string }> = [];
          for (const list of [peopleFromContacts, peopleFromCA, peopleFromDR, peopleFromSearch]) {
            for (const p of list) {
              if (!p.name) continue;
              const key = p.name.toLowerCase().trim();
              if (seen.has(key)) continue;
              seen.add(key);
              allPeople.push(p);
            }
          }

          if (allPeople.length === 0) {
            return <p className="text-muted-foreground italic">Details not available</p>;
          }

          return (
            <div className="space-y-2">
              {allPeople.map((p, i) => (
                <div key={i} className="flex items-start gap-3 p-2 rounded border bg-muted/30 text-sm">
                  <User className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <span className="font-medium">{p.name}</span>
                    {p.position && <span className="text-muted-foreground ml-2">— {p.position}</span>}
                    <div className="flex flex-wrap gap-3 mt-0.5">
                      {p.email && <a className="text-blue-600 hover:underline text-xs" href={`mailto:${p.email}`}>{p.email}</a>}
                      {p.phone && <a className="text-blue-600 hover:underline text-xs" href={`tel:${p.phone}`}>{p.phone}</a>}
                      {p.linkedinUrl && <a className="text-blue-600 hover:underline text-xs" href={p.linkedinUrl} target="_blank" rel="noopener noreferrer">LinkedIn</a>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </section>

      {/* 8. Locations (with Map) */}
      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">8. Locations</h4>
        <div className="space-y-3">
          {officeLocations.length > 0 ? (
            <CollapsibleList
              items={officeLocations.map((loc: any) => typeof loc === 'string' ? JSON.stringify({ name: loc }) : JSON.stringify(loc))}
              limit={6}
              renderItem={(raw) => {
                let loc: any;
                try { loc = JSON.parse(raw); } catch { loc = { name: raw }; }
                return (
                  <div className="flex items-start gap-3 p-2 rounded border bg-muted/30 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <span className="font-medium">{loc.city || loc.name}</span>
                      {loc.address && <span className="text-muted-foreground ml-2">— {loc.address}</span>}
                      {loc.phone && (
                        <span className="ml-2">
                          <a className="text-blue-600 hover:underline" href={`tel:${loc.phone}`}>{loc.phone}</a>
                        </span>
                      )}
                    </div>
                  </div>
                );
              }}
            />
          ) : searchData.locations && searchData.locations.length > 0 ? (
            <BulletList items={searchData.locations} />
          ) : (
            <p className="text-muted-foreground italic">{address || 'Details not available'}</p>
          )}

          {/* Google Map embed */}
          {placeId && GOOGLE_PLACES_API_KEY && GOOGLE_PLACES_API_KEY.trim() ? (
            <div className="rounded-lg overflow-hidden border-2 shadow-sm">
              <iframe width="100%" height="300" frameBorder="0" style={{ border: 0 }}
                src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_PLACES_API_KEY}&q=place_id:${placeId}`}
                allowFullScreen title="Company Location" />
            </div>
          ) : address ? (
            <div className="rounded-lg overflow-hidden border-2 shadow-sm">
              <iframe width="100%" height="300" frameBorder="0" style={{ border: 0 }}
                src={`https://www.google.com/maps?q=${encodeURIComponent(address)}&z=15&output=embed`}
                allowFullScreen title="Company Location" />
            </div>
          ) : (
            <div className="p-4 border rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Map not available</p>
              {searchData.places?.mapsUrl && (
                <a href={searchData.places.mapsUrl} target="_blank" rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm font-medium">Open in Google Maps →</a>
              )}
            </div>
          )}
        </div>
      </section>

      {/* 9. Social Media */}
      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">9. Social Media</h4>
        {(mergedSocial.facebook || mergedSocial.linkedin || mergedSocial.twitter || mergedSocial.instagram) && (
          <p className="text-[15px] mb-3">Maintains a presence on several social media platforms:</p>
        )}
        <div className="flex gap-3 flex-wrap">
          {mergedSocial.facebook ? (
            <a className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-sm" href={mergedSocial.facebook} target="_blank" rel="noopener noreferrer">Facebook</a>
          ) : <span className="text-muted-foreground italic">Facebook: Details not available</span>}

          {mergedSocial.linkedin ? (
            <a className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-sm" href={mergedSocial.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>
          ) : <span className="text-muted-foreground italic">LinkedIn: Details not available</span>}

          {mergedSocial.twitter && (
            <a className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-sm" href={mergedSocial.twitter} target="_blank" rel="noopener noreferrer">Twitter</a>
          )}
          {mergedSocial.instagram && (
            <a className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-sm" href={mergedSocial.instagram} target="_blank" rel="noopener noreferrer">Instagram</a>
          )}
        </div>
      </section>

      {/* 10. Support Channels */}
      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">10. Support Channels</h4>
        <p className="text-[15px]">{formatSupportNarrative(searchData.supportChannels, phone, email, altPhone)}</p>
      </section>

      {/* 11. Recent Company News */}
      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">11. Recent Company News</h4>
        {searchData.recentNews && searchData.recentNews.length > 0 ? (
          <ul className="space-y-2">
            {searchData.recentNews.map((news, i) => {
              const newsText = safeStringify(news);
              if (!newsText) return null;
              return (
                <li key={i} className="flex items-start gap-2 p-2 bg-blue-50 dark:bg-blue-950/30 rounded">
                  <span className="mt-0.5">📰</span>
                  <span>{newsText}</span>
                </li>
              );
            }).filter(Boolean)}
          </ul>
        ) : (
          <p className="text-muted-foreground italic">Details not available</p>
        )}
      </section>

      {/* 11b. Current Job Openings */}
      {jobOpenings.length > 0 && (
        <section className="border-t pt-6">
          <h4 className="font-bold mb-2 flex items-center gap-2"><Briefcase className="h-4 w-4" /> Current Job Openings</h4>
          <BulletList items={jobOpenings} />
        </section>
      )}

      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">12. Customer Reviews Summary</h4>
        {rating ? (
          <div className="mb-3 flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
            <span className="text-[15px]">{rating} / 5</span>
            {reviewsCount ? <span className="text-muted-foreground text-sm">({reviewsCount} reviews)</span> : null}
          </div>
        ) : null}
        {reviews && reviews.length > 0 ? (
          <div className="space-y-3">
            {reviews.slice(0, 3).map((rv: any, idx: number) => (
              <div key={idx} className="border-l-2 border-primary/20 pl-3 py-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`h-3 w-3 ${i < (rv.rating ?? 0) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  <span className="text-xs text-muted-foreground font-medium">{rv.author_name || 'Anonymous'}</span>
                </div>
                {rv.text && <p className="text-sm text-foreground/80">{rv.text}</p>}
              </div>
            ))}
          </div>
        ) : (() => {
          const testimonials = dr?.clientTestimonials || ca?.clientTestimonials || [];
          if (testimonials.length > 0) {
            return (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground mb-1">From client testimonials:</p>
                {testimonials.slice(0, 5).map((t: any, idx: number) => (
                  <div key={idx} className="border-l-2 border-primary/20 pl-3 py-1">
                    <p className="text-sm text-foreground/80">{typeof t === 'string' ? t : t.text || t.quote || safeStringify(t)}</p>
                    {typeof t === 'object' && t.author && <p className="text-xs text-muted-foreground mt-1">— {t.author}</p>}
                  </div>
                ))}
              </div>
            );
          }
          return !rating ? <p className="text-muted-foreground italic">Details not available</p> : null;
        })()}
      </section>

      {/* 13. Data Verification */}
      <section className="border-t pt-6">
        <h4 className="font-bold mb-2">13. Data Verification</h4>
        <div className="text-sm text-muted-foreground space-y-1">
          {searchData.linkedIn?.dataVerifiedAt && (
            <p>✓ LinkedIn data verified: {formatDateYMD(searchData.linkedIn.dataVerifiedAt)}</p>
          )}
          {sources.length > 0 && <p>✓ Sources: {sources.join(', ')}</p>}
          {searchData.web_search_updated_at && (
            <p>✓ Last updated: {formatDateYMD(searchData.web_search_updated_at)}</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default WebSearchDetails;
